<template>
    <div class="workpost-scan-products">
        <loader
            v-if="loading"
            :cover="true"
        />

        <div class="workpost-header">
            <div class="top-buttons">
                <ui5-button
                    id="back-to-workstations"
                    @click="backToworkstations()"
                >
                    {{ trans('pages.workpost_scan_product.back') }}
                </ui5-button>
                <ui5-button
                    id="to-overview"
                    @click="toOverview()"
                >
                    {{ trans('pages.workpost_scan_product.to_product_overview') }}
                </ui5-button>
                <p v-if="timer">
                    {{ timer.shortDescription }}
                </p>
            </div>

            <div class="right-column">
                <div>
                    <div class="validate-form">
                        <ui5-input
                            :disabled="loading || (timer && !timer.finished)"
                            type="Text"
                            :value="formProductNr"
                            @input="formProductNr = $event.target.value"
                            @keyup.enter="validateProductNr(formProductNr)"
                        />
                        <br />
                        <ui5-button
                            :disabled="loading || (timer && !timer.finished)"
                            @click="validateProductNr(formProductNr)"
                        >
                            {{ trans('pages.workpost_scan_product.validate') }}
                        </ui5-button>
                    </div>
                    <div class="camera-icon">
                        <ui5-button
                            :disabled="loading || (timer && !timer.finished)"
                            @click="scan()"
                        >
                            <i class="fa fa-camera fa-3x"></i>
                        </ui5-button>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="timer"
            v-if="!loading && timer && !timer.finished"
        >
            <product-timer
                :loading="loading"
                @finish="onFinish"
                @finish-check-colli="onFinishCheckColli"
                @pause="onPause"
                @unpause="onUnpause"
                @reporting-error="onErrorReporting"
                :can-report-error="true"
                :timer="timer"
                :all-finished="getAllFinished()"
                :check-colli-count="workstation.checkColliCount"
            ></product-timer>
            <print-actions-buttons
                v-if="workstation.canPrintIdentificationLabel || workstation.canPrintPrSticker"
                :workstation="workstation"
                :product="product"
                @print-identification-label="printIdentificationLabel"
                @print-pr-sticker="printPrSticker"
            ></print-actions-buttons>
            <div
                class="action-buttons"
                v-if="actionButtons && actionButtons.length && timer"
            >
                <ui5-button
                    v-for="(button, buttonIndex) in actionButtons"
                    :key="buttonIndex"
                    @click="executeButton(button)"
                >
                    {{ button.label }}
                </ui5-button>
            </div>
        </div>

        <div
            class="product-description"
            v-if="
                !loading &&
                !workstationScreen &&
                timer &&
                (!timer.finished || !timer.id)
            "
            v-html="timer.description"
        ></div>

        <ui5-card
            class="workstation-screen"
            ref="workstationScreen"
            v-if="workstationScreen"
        >
            <workstation-screen-canvas
                ref="canvas"
                :width="width"
                :height="height"
                :scale="workstationScreenScale"
                :sawlines="sawlines"
                :datafields="workstationScreen.datafields"
                :screen-items="screenItems"
                :checkbox-click="checkboxClick"
                :background-image="backgroundImage"
                :product-description="timer.description"
                :show-sawlines-symbol="false"
                :product-image="productImage"
            ></workstation-screen-canvas>
        </ui5-card>

        <ui5-dialog
            ref="colliDialog"
            :header-text="trans('pages.workpost_scan_product.colli_popup_title')"
            class="colli-dialog"
        >
            <div class="colli-dialog-content">
                <div class="product-info">
                    {{ trans('pages.workpost_scan_product.colli_product_label', { productId: product ? product.productId : '-' }) }}
                </div>
                <div class="colli-display">
                    <div class="colli-label">
                        {{ trans('pages.workpost_scan_product.colli_current_label') }}
                    </div>
                    <div class="colli-value">
                        {{ colliCount }}
                    </div>
                </div>
                <div class="colli-controls">
                    <ui5-button
                        @click="colliCount = Math.max(1, colliCount - 1)"
                        class="control-btn"
                    >
                        <i class="fa fa-minus"></i>
                    </ui5-button>
                    <ui5-button
                        @click="colliCount++"
                        class="control-btn"
                    >
                        <i class="fa fa-plus"></i>
                    </ui5-button>
                </div>
                <div class="dialog-actions">
                    <ui5-button @click="cancelColli()">
                        {{ trans('pages.workpost_scan_product.colli_cancel') }}
                    </ui5-button>
                    <ui5-button @click="confirmColli()">
                        {{ trans('pages.workpost_scan_product.colli_confirm') }}
                    </ui5-button>
                </div>
            </div>
        </ui5-dialog>
    </div>
</template>
<script>
import ProductTimer from './ProductTimer';
import PrintActionsButtons from './PrintActionsButtons';
import Loader from '../loader/Loader';
import localizationMixin from '../../mixins/localization';
import productionTimesApi from '../../api/productionTimes';
import productApi from '../../api/product';
import workstationScreenApi from '../../api/workstationScreen';
import sawlineApi from '../../api/sawline';
import { getDatafieldLabel } from '../../shared/datafield';
import IsDebugMixin from '../../mixins/is-debug';
import {
    SAWLINE_CHAR,
    specialOptions,
    UNICODE_FONTS,
} from '../../shared/workstationScreens';
import { showErrorMessage, showInfoMessage } from '../../dialogs';
import { productMocker } from '../../shared/qrMockers';
import WorkstationScreenCanvas from '../workstationScreens/WorkstationScreenCanvas';
import exportApi from '../../api/export';

const WORKSTATION_SCREEN_WIDTH = 1280;
const WORKSTATION_SCREEN_HEIGHT = 720;

// Track the currently active component instance to prevent race conditions when
// destroying the old instance (problem with cleanup of event listeners during switching between workstations)
let _currentWorkstationInstance = null;

export default {
    mixins: [IsDebugMixin, localizationMixin],
    components: {
        ProductTimer,
        PrintActionsButtons,
        Loader,
        WorkstationScreenCanvas,
    },
    props: ['workstation', 'actionButtons'],
    data() {
        return {
            timer: null,
            formProductNr: null,
            product: null,
            sawlines: [],
            workstationScreen: null,
            workstationScreenScale: 1,
            loading: false,
            backgroundImage: null,
            productImage: null,
            colliCount: 0,
        };
    },
    computed: {
        width: () => WORKSTATION_SCREEN_WIDTH,
        height: () => WORKSTATION_SCREEN_HEIGHT,
        screenItems() {
            if (!this.workstationScreen) {
                return [];
            }
            const datafields = this.workstationScreen.datafields.map((x) => {
                return {
                    ...x,
                    type: 'datafield',
                    selected: false,
                    value: x.datafieldDetails
                        ? x.datafieldDetails.fieldvalue || ''
                        : '',
                    valueDescription: x.datafieldDetails
                        ? x.datafieldDetails.fieldvaluedescription || ''
                        : '',
                };
            });
            const sawlines = this.workstationScreen.sawlines.map((x) => {
                return {
                    ...x,
                    type: 'sawline',
                    selected: false,
                    value: this.getSawlineValue(x),
                };
            });

            const specials = this.workstationScreen.specials.map((x) => {
                return {
                    ...x,
                    type: 'special',
                    selected: false,
                    value: this.getSpecialValue(x.key),
                };
            });

            return [...datafields, ...sawlines, ...specials];
        },
        showScreen() {
            return (
                !this.loading &&
                this.timer &&
                (!this.timer.finished || !this.timer.id)
            );
        },
        UNICODE_FONTS: () => UNICODE_FONTS,
        SAWLINE_CHAR: () => SAWLINE_CHAR,
        specialOptions: () => specialOptions,
        selectedPrinterPostParameter() {
            return this.$store.getters[
                'printers/getSelectedPrinterPostParameter'
            ]();
        },
    },
    methods: {
        getDatafieldLabel,
        getAllFinished() {
            if (!this.workstationScreen) {
                return true;
            }

            const items = this.screenItems.filter((x) => x.showCheckbox);

            return items.every((x) => x.selected);
        },
        checkboxClick(item, newValue) {
            item.selected = newValue;
            this.$forceUpdate();
        },
        getSpecialValue(key) {
            if (key == 'product.note') {
                return this.product.note;
            }
            if (key == 'product.count') {
                return this.product.count || '';
            }
            if (key == 'product.color') {
                if (this.product.color) {
                    const poedernr = this.product.color.poedernr || '';
                    const producent = this.product.color.producent || '';
                    if (poedernr && producent) {
                        return `${poedernr} (${producent})`;
                    }
                    return poedernr || producent || '';
                }
                return '';
            }
            return '?';
        },
        getSawlineValue(sawline) {
            if (
                !sawline.sawlineDetails ||
                (!sawline.sawlineDetails.value && !sawline.sawlineDetails.unit)
            ) {
                return '';
            }
            let label = sawline.sawlineDetails.value;
            if (sawline.sawlineDetails.unit) {
                if (label) {
                    label += ' ';
                }
                label += sawline.sawlineDetails.unit;
            }
            return label;
        },
        handleBeforeRouteLeave(to, from, next) {
            if (this.timer && !this.timer.finished) {
                this.onPause();
            }
            next();
        },
        backToworkstations() {
            this.$router.push('/workpostsmenu');
        },
        toOverview() {
            this.$router.push(
                '/workstation/' + this.workstation.workstationTypeId
            );
        },
        onPause() {
            const timer = this.timer;
            this.loading = true;
            productionTimesApi
                .finishProduct(timer.id, true)
                .then(() => {
                    showInfoMessage('De productietijd werd opgeslagen');
                    this.loading = false;
                    this.timer = null;
                    this.workstationScreen = null;
                })
                .catch(() => {
                    showErrorMessage('Onbekende fout bij opslaan');
                    timer.paused = false;
                    this.loading = false;
                });
        },
        onUnpause() {
            this.validateProductNr(this.timer.productNr);
        },
        onFinishCheckColli() {
            this.colliCount = this.product.colli || 1;
            this.$refs.colliDialog.show();
        },
        cancelColli() {
            this.$refs.colliDialog.close();
            this.timer.finished = false;
        },
        confirmColli() {
            this.$refs.colliDialog.close();
            this.loading = true;
            productApi
                .setAdditionalInfoToProduct(
                    this.product.productId,
                    this.product.boxnr || '',
                    this.colliCount
                )
                .then(() => {
                    this.timer.finished = true;
                    this.onFinish();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.workpost_scan_product.colli_error')
                    );
                    this.timer.finished = false;
                    this.loading = false;
                });
        },
        onFinish() {
            const timer = this.timer;
            return new Promise((resolve, reject) => {
                if (!timer || timer.paused) {
                    resolve();
                } else {
                    this.loading = true;
                    productionTimesApi
                        .finishProduct(timer.id, false)
                        .then(() => {
                            showInfoMessage('De productietijd werd opgeslagen');
                            this.loading = false;
                            this.$store.commit(
                                'general/setPageTitle',
                                this.workstation.label,
                                { root: true }
                            );
                            this.timer = null;
                            this.workstationScreen = null;
                            resolve();
                        })
                        .catch(() => {
                            showErrorMessage('Onbekende fout bij opslaan');
                            timer.finished = false;
                            this.loading = false;
                            reject();
                        });
                }
            });
        },
        onErrorReporting() {
            this.onPause();
            this.$router.push('/productsaw/' + this.product.productId);
        },
        executeButton(button) {
            const productNr = this.timer.productNr;
            const action = button.action;
            action(productNr);
        },
        validateProductNr(productNr) {
            this.loading = true;
            const productId = parseInt(productNr.toString().replace(/\D/g, ''));

            return Promise.all([
                this.fetchProductionTimes(productId, productNr),
                this.fetchProductDetails(productId),
                this.fetchSawlines(productId),
            ])
                .then(() => {
                    this.fetchWorkstationScreen();

                    const image = new window.Image();
                    image.onerror = () => {
                        this.productImage = null;
                    };
                    image.onload = () => {
                        this.productImage = image;
                    };
                    image.src =
                        Config.url +
                        '/product/customImage/?productId=' +
                        productId;
                })
                .catch(() => {
                    showErrorMessage('Onbekende fout');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchProductionTimes(productId, productNr) {
            const readOnly = this.$route.query.readOnly;
            return productionTimesApi
                .fetchProduct(productId, this.workstation.id, readOnly)
                .catch((err) => {
                    const errorData = err.response.data;
                    showErrorMessage(errorData.message);
                    this.loading = false;
                    this.timer = null;
                    if (errorData.code === 'no.minutes') {
                        this.timer = {
                            finished: true,
                            productNr: errorData.productNr,
                            description: errorData.description,
                        };
                    }

                    // Stop the chain
                    return new Promise(() => null);
                })
                .then((response) => {
                    const product = response.data.data;
                    this.timer = {
                        productNr: productNr,
                        minutes: product.targetMinutes,
                        shortDescription: product.shortDescription,
                        description: product.description,
                        remainingSeconds: null,
                        finished: false,
                        paused: false,
                        id: product.historyRecord.ID,
                        previousSeconds: product.previousSeconds,
                    };
                    this.$store.commit(
                        'general/setPageTitle',
                        this.workstation.label + ' ' + product.productId,
                        { root: true }
                    );

                    this.formProductNr = '';
                });
        },
        fetchProductDetails(productId) {
            return productApi
                .fetchProductDetails(productId, false)
                .then((response) => {
                    this.product = response.data.data;
                });
        },
        fetchSawlines(productId) {
            return sawlineApi
                .fetchSawlines(productId, true)
                .then((response) => (this.sawlines = response.data.data));
        },
        fetchWorkstationScreen() {
            return workstationScreenApi
                .fetchByWorkstationProducttype(
                    this.workstation.id,
                    this.product.producttypeId
                )
                .catch(() => {
                    showInfoMessage('Geen werkpost scherm gevonden');
                    this.loading = false;

                    // Stop the chain
                    return new Promise(() => null);
                })
                .then((response) => {
                    this.workstationScreen = response.data.data;

                    this.workstationScreen.datafields =
                        this.workstationScreen.datafields.map((datafield) => ({
                            ...datafield,
                            finished: false,
                            datafieldDetails: this.product.datafields.find(
                                (productDatafield) =>
                                    parseInt(productDatafield.datafieldId) ===
                                    datafield.datafieldId
                            ),
                        }));

                    this.workstationScreen.sawlines =
                        this.workstationScreen.sawlines.map((sawline) => ({
                            ...sawline,
                            sawlineDetails: this.sawlines.find(
                                (sawlineDetails) =>
                                    parseInt(sawlineDetails.id) ===
                                    sawline.sawlineId
                            ),
                        }));

                    if (this.workstationScreen.backgroundImageUrl) {
                        const image = new window.Image();
                        image.onerror = () => {
                            showErrorMessage(
                                'Ongeldige achtergrondafbeelding in de databank'
                            );
                            this.backgroundImage = null;
                        };
                        image.onload = () => {
                            this.backgroundImage = image;
                        };
                        image.src = this.workstationScreen.backgroundImageUrl;
                    }

                    this.$nextTick(this.calculateScale);
                });
        },
        calculateScale() {
            if (this.$refs.workstationScreen) {
                const canvasRelativeTop = 36;
                const headerHeight = 70;
                const marginLeft = 280;
                const availableHeight =
                    document.documentElement.offsetHeight -
                    this.$refs.workstationScreen.offsetTop +
                    canvasRelativeTop +
                    headerHeight;
                const availableWidth =
                    document.documentElement.offsetWidth - marginLeft;
                const heightScale = availableHeight / WORKSTATION_SCREEN_HEIGHT;
                const widthScale = availableWidth / WORKSTATION_SCREEN_WIDTH;
                this.workstationScreenScale = Math.min(heightScale, widthScale);
            }
        },
        scan() {
            this.$bus.$emit('qr_scanner.start', 'workstation', {
                singleScan: true,
                defaultCamera: 'front',
                mocker: productMocker,
            });
        },
        handleScan(key, value) {
            if (key !== 'workstation') {
                return;
            }
            if (_currentWorkstationInstance !== this) {
                return;
            }
            this.handleWorkstationScan(value);
        },
        handleWorkstationScan(value) {
            let decoded = null;
            try {
                decoded = JSON.parse(value);
            } catch (error) {}

            if (decoded && decoded.context === 'product') {
                this.validateProductNr(decoded.productId);
            } else {
                showErrorMessage(
                    'Deze QR-code dient niet om producten te identificeren',
                    null,
                    1500
                );
            }
        },
        exportIdentificationLabel() {
            if (
                !this.product ||
                !this.workstation.canPrintIdentificationLabel
            ) {
                return;
            }

            this.loading = true;

            exportApi
                .exportProductIdentificationLabel(this.product.productId)
                .then((response) => {
                    // Create a URL to the blob data
                    const blobUrl = window.URL.createObjectURL(
                        new Blob([response.data])
                    );

                    // Create a temporary link element and trigger a click event
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.target = '_blank';
                    link.download = `identificatie-sticker-${this.product.productId}.pdf`;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();

                    // Clean up
                    window.URL.revokeObjectURL(blobUrl);
                    document.body.removeChild(link);

                    this.loading = false;
                })
                .catch((error) => {
                    console.error(error);

                    showErrorMessage(
                        'Identificatie sticker kon niet worden gedownload'
                    );

                    this.loading = false;
                });
        },
        printIdentificationLabel() {
            if (KeyHelper.isPressed('Shift')) {
                return this.exportIdentificationLabel();
            }

            if (
                !this.product ||
                !this.workstation.canPrintIdentificationLabel
            ) {
                return;
            }

            this.loading = true;

            exportApi
                .printProductIdentificationLabel(
                    this.product.productId,
                    this.selectedPrinterPostParameter
                )
                .then(() => {
                    showInfoMessage(this.trans('pages.workpost_scan_product.identification_label_printed'));

                    this.loading = false;
                })
                .catch((error) => {
                    console.error(error);

                    showErrorMessage(
                        this.trans('pages.workpost_scan_product.identification_label_print_error')
                    );

                    this.loading = false;
                });
        },
        printPrSticker() {
            if (!this.product || !this.workstation.canPrintPrSticker) {
                return;
            }

            if (KeyHelper.isPressed('Shift')) {
                return window.open(
                    Config.url +
                        '/export/exportProductionLabel/' +
                        '?productId=' +
                        this.product.productId
                );
            }

            this.loading = true;

            exportApi
                .printProductionLabel(
                    this.product.productId,
                    this.selectedPrinterPostParameter
                )
                .then(() => {
                    showInfoMessage(this.trans('pages.workpost_scan_product.pr_sticker_printed'));
                    this.loading = false;
                })
                .catch((error) => {
                    console.error(error);
                    showErrorMessage(this.trans('pages.workpost_scan_product.pr_sticker_print_error'));
                    this.loading = false;
                });
        },
    },
    mounted() {
        _currentWorkstationInstance = this;
        this.$bus.$on('qr_scanner.on_scan', this.handleScan);

        this.$store.commit('general/setPageTitle', this.workstation.label, {
            root: true,
        });

        this.$bus.$on('window-resize', this.calculateScale);
    },
    beforeDestroy() {
        if (_currentWorkstationInstance === this) {
            _currentWorkstationInstance = null;
        }
        this.$bus.$off('qr_scanner.on_scan', this.handleScan);
        this.$bus.$off('window-resize', this.calculateScale);
    },
    beforeRouteLeave(to, from, next) {
        this.handleBeforeRouteLeave(to, from, next);
    },
};
</script>
